import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  RefreshCw,
  Database,
  CreditCard,
  Settings,
  TrendingUp
} from 'lucide-react';

interface HealthStatus {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  checks: {
    database: { status: 'pass' | 'fail'; response_time_ms?: number; error?: string };
    stripe: { status: 'pass' | 'fail'; error?: string };
    environment: { status: 'pass' | 'fail'; missing_vars?: string[] };
  };
  uptime_ms?: number;
}

interface ErrorLog {
  id: string;
  error_type: string;
  severity: string;
  message: string;
  created_at: string;
  resolved: boolean;
}

export function HealthMonitor() {
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [recentErrors, setRecentErrors] = useState<ErrorLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchHealthStatus = async () => {
    try {
      setRefreshing(true);
      const response = await fetch('/api/health');
      const data = await response.json();
      setHealthStatus(data);
    } catch (error) {
      console.error('Failed to fetch health status:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const fetchRecentErrors = async () => {
    try {
      const { data, error } = await supabase
        .from('application_errors')
        .select('id, error_type, severity, message, created_at, resolved')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setRecentErrors(data || []);
    } catch (error) {
      console.error('Failed to fetch recent errors:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchHealthStatus(), fetchRecentErrors()]);
      setLoading(false);
    };

    loadData();
    
    // Refresh health status every 30 seconds
    const interval = setInterval(fetchHealthStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'pass':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'degraded':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'unhealthy':
      case 'fail':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variant = status === 'healthy' || status === 'pass' ? 'default' : 
                    status === 'degraded' ? 'secondary' : 'destructive';
    return <Badge variant={variant}>{status}</Badge>;
  };

  const getSeverityBadge = (severity: string) => {
    const variant = severity === 'low' ? 'secondary' : 
                    severity === 'medium' ? 'default' : 'destructive';
    return <Badge variant={variant}>{severity}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">System Health</h2>
        <Button 
          onClick={fetchHealthStatus} 
          disabled={refreshing}
          size="sm"
          variant="outline"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Overall Status */}
      {healthStatus && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                {getStatusIcon(healthStatus.status)}
                System Status
              </CardTitle>
              {getStatusBadge(healthStatus.status)}
            </div>
            <CardDescription>
              Last checked: {new Date(healthStatus.timestamp).toLocaleString()}
              {healthStatus.uptime_ms && (
                <span className="ml-2">
                  (Response time: {healthStatus.uptime_ms}ms)
                </span>
              )}
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      <Tabs defaultValue="services" className="w-full">
        <TabsList>
          <TabsTrigger value="services">Services</TabsTrigger>
          <TabsTrigger value="errors">Recent Errors</TabsTrigger>
          <TabsTrigger value="metrics">Metrics</TabsTrigger>
        </TabsList>

        <TabsContent value="services" className="space-y-4">
          {healthStatus && (
            <>
              {/* Database Status */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Database className="w-5 h-5" />
                    Database
                    {getStatusBadge(healthStatus.checks.database.status)}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {healthStatus.checks.database.response_time_ms && (
                    <p className="text-sm text-gray-600">
                      Response time: {healthStatus.checks.database.response_time_ms}ms
                    </p>
                  )}
                  {healthStatus.checks.database.error && (
                    <Alert className="mt-2">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        {healthStatus.checks.database.error}
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Stripe Status */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <CreditCard className="w-5 h-5" />
                    Stripe
                    {getStatusBadge(healthStatus.checks.stripe.status)}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {healthStatus.checks.stripe.error && (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        {healthStatus.checks.stripe.error}
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Environment Status */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Settings className="w-5 h-5" />
                    Environment
                    {getStatusBadge(healthStatus.checks.environment.status)}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {healthStatus.checks.environment.missing_vars && 
                   healthStatus.checks.environment.missing_vars.length > 0 && (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        Missing environment variables: {healthStatus.checks.environment.missing_vars.join(', ')}
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="errors" className="space-y-4">
          {recentErrors.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                  <p>No recent errors</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            recentErrors.map((error) => (
              <Card key={error.id}>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline">{error.error_type}</Badge>
                        {getSeverityBadge(error.severity)}
                        {error.resolved && <Badge variant="default">Resolved</Badge>}
                      </div>
                      <p className="text-sm font-medium mb-1">{error.message}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(error.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="metrics" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Application Metrics
              </CardTitle>
              <CardDescription>
                Key performance indicators and system metrics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Metrics dashboard coming soon...
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
